import { defineStore } from "pinia";
import apiClient from "@/apiClient";
import { useAuthStore } from "@/stores/AuthStore";

const POLL_INTERVAL_MS = 30000;

const emptySummary = () => ({
  online: 0,
  idle: 0,
  offline: 0,
  total: 0,
});

export const usePresenceStore = defineStore("presence", {
  state() {
    return {
      entries: [],
      summary: emptySummary(),
      loading: false,
      loaded: false,
      generatedAt: null,
      pollTimer: null,
    };
  },

  actions: {
    async load() {
      const auth = useAuthStore();
      if (!auth.isLoggedIn) {
        this.reset();
        return;
      }

      if (this.loading) {
        return;
      }

      this.loading = true;
      try {
        const { data } = await apiClient.get("/api/presence");
        this.entries = data?.users || [];
        this.summary = data?.summary || emptySummary();
        this.generatedAt = data?.generated_at || null;
        this.loaded = true;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    startPolling() {
      const auth = useAuthStore();
      if (!auth.isLoggedIn || typeof window === "undefined") {
        return;
      }

      this.stopPolling();
      void this.load();
      this.pollTimer = window.setInterval(() => {
        void this.load();
      }, POLL_INTERVAL_MS);
    },

    stopPolling() {
      if (this.pollTimer && typeof window !== "undefined") {
        window.clearInterval(this.pollTimer);
      }
      this.pollTimer = null;
    },

    reset() {
      this.entries = [];
      this.summary = emptySummary();
      this.loading = false;
      this.loaded = false;
      this.generatedAt = null;
      this.stopPolling();
    },
  },

  getters: {
    onlineEntries: (state) => state.entries.filter((entry) => entry.status === "online"),
    activeEntries: (state) => state.entries.filter((entry) => entry.status === "online" || entry.status === "idle"),
  },
});
